import React, {useCallback, useEffect, useState} from 'react';
import {Box, Button, Modal, TextField, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {DateTime} from 'luxon';
import styles from '../../styles';
import {updateShopifySubscription} from '../../api/adminAPI';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  shopifySubscriptionId?: number;
  currentExpiringAt?: string | null;
  onStart?: () => void;
  onSuccess?: () => Promise<void>;
  onError?: () => void;
}

const DATE_FORMAT = 'yyyy-MM-dd';

const EditShopifySubscriptionExpiringAtModal: React.FC<Props> = ({
  open,
  setOpen,
  shopifySubscriptionId,
  currentExpiringAt,
  onStart,
  onSuccess,
  onError,
}) => {
  const {t} = useTranslation();
  const dateFormat = t(
    'admin.editShopifySubscriptionExpiringAt.dateFormat',
  ) as string;

  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  // Seed the field with the current expiringAt whenever the modal opens.
  useEffect(() => {
    if (open) {
      setValue(
        currentExpiringAt
          ? DateTime.fromISO(currentExpiringAt).toFormat(DATE_FORMAT)
          : '',
      );
      setError('');
    }
  }, [open, currentExpiringAt]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleSubmit = useCallback(async () => {
    if (!shopifySubscriptionId) {
      window.alert(t('membership.unknownSubscription'));
      return;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      setError(
        t('admin.editShopifySubscriptionExpiringAt.dateRequired', {
          dateFormat,
        }) as string,
      );
      return;
    }
    if (!DateTime.fromFormat(trimmed, DATE_FORMAT).isValid) {
      setError(
        t('admin.editShopifySubscriptionExpiringAt.invalidDateFormat', {
          dateFormat,
        }) as string,
      );
      return;
    }

    setPending(true);
    if (onStart) {
      onStart();
    }
    try {
      const res = await updateShopifySubscription({
        id: shopifySubscriptionId,
        expiringAt: trimmed,
      });
      setPending(false);
      if (res) {
        setOpen(false);
        if (onSuccess) {
          await onSuccess();
        }
      } else if (onError) {
        onError();
      }
    } catch (e) {
      setPending(false);
      if (onError) {
        onError();
      }
    }
  }, [
    shopifySubscriptionId,
    value,
    dateFormat,
    onStart,
    onSuccess,
    onError,
    setOpen,
    t,
  ]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={styles.modal}>
        <Typography variant="h4" sx={{mb: 2}}>
          {t('admin.editShopifySubscriptionExpiringAt.title')}
        </Typography>
        <TextField
          value={value}
          onChange={e => setValue(e.target.value)}
          label={t('admin.editShopifySubscriptionExpiringAt.expiringAtLabel', {
            dateFormat,
          })}
          variant="outlined"
          fullWidth
          placeholder={dateFormat}
          sx={{mt: 1, mb: 2}}
          error={!!error}
          helperText={error || ''}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
          }}>
          <Button
            variant="outlined"
            onClick={handleSubmit}
            disabled={pending}
            sx={{mr: 1}}>
            {t('general.submit')}
          </Button>
          <Button variant="contained" onClick={handleClose}>
            {t('general.close')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditShopifySubscriptionExpiringAtModal;
